import _, { Dictionary } from 'underscore';
import React, { createRef, useEffect } from 'react';
import { Divider, useArrowNavigationGroup, mergeClasses } from '@fluentui/react-components';

import { ToolGroupConfig } from './types';
import { ToolsListGroup } from './tools-list-group';
import { useListStyles } from './styles';

const useGroupScrollObserver = (
  groups: ToolGroupConfig[],
  containerRef: React.RefObject<HTMLDivElement>,
  groupRefs: Dictionary<React.RefObject<HTMLDivElement>>,
  // eslint-disable-next-line no-unused-vars
  onFirstVisibleGroupChange: (group: string) => void,
) => {
  const [visibleGroups, setVisibleGroups] = React.useState<{ [x: string]: boolean }>(_.reduce(groups, (prev, group) => _.extend(prev, { [group.key]: false }), {}));

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      const delta = _.reduce(entries, (prev, entry) => {
        const key = entry.target.parentElement.className.split(' ')[1];

        return _.extend(prev, { [key]: entry.isIntersecting });
      }, {});

      const newVisibleGroups = { ...visibleGroups, ...delta };

      if (!_.isEqual(visibleGroups, newVisibleGroups)) {
        setVisibleGroups(newVisibleGroups);
      }
    }, {
      root: containerRef.current,
      threshold: 0.5,
    });

    _.forEach(groupRefs, (ref) => observer.observe(ref.current.children[0]));

    return () => observer.disconnect();
  }, [containerRef, groupRefs, visibleGroups]);

  useEffect(() => {
    const firstVisibleGroup = _.find(groups, (group) => visibleGroups[group.key]);

    if (firstVisibleGroup) {
      onFirstVisibleGroupChange(firstVisibleGroup.key);
    }
  }, [groups, visibleGroups, onFirstVisibleGroupChange]);
};

export const ToolsList: React.FC<{
  groups: ToolGroupConfig[];
  groupRefs: Dictionary<React.RefObject<HTMLDivElement>>;
  searchValue: string;
  // eslint-disable-next-line no-unused-vars
  onFirstVisibleGroupChange: (group: string) => void;
}> = ({
  groups, groupRefs, searchValue, onFirstVisibleGroupChange,
}) => {
  const classes = useListStyles();
  const navigationProps = useArrowNavigationGroup({ axis: 'grid-linear', memorizeCurrent: true });

  const containerRef = createRef<HTMLDivElement>();
  useGroupScrollObserver(groups, containerRef, groupRefs, onFirstVisibleGroupChange);

  return (
    <div
      ref={containerRef}
      className={mergeClasses('tools-list', classes.root)}
      {...navigationProps}
    >
      {_.map(groups, (group, index) => (
        <>
          {index !== 0 && <Divider className={classes.divider} />}
          <ToolsListGroup
            key={group.key}
            ref={groupRefs[group.key]}
            group={group}
            searchValue={searchValue}
          />
        </>
      ))}
    </div>
  );
};
